/**
 * Web Authentication Service using Azure AD (MSAL Browser)
 * Browser-based Azure AD authentication
 */

import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../config/authConfig";

class WebAuthService {
  constructor() {
    this.msalInstance = null;
    this.account = null;
    this.initializePromise = null;
  }

  /**
   * Initialize MSAL instance
   */
  async initialize() {
    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = (async () => {
      try {
        // Create MSAL instance
        this.msalInstance = new PublicClientApplication(msalConfig);

        // Initialize MSAL
        await this.msalInstance.initialize();

        // Handle redirect response
        try {
          const response = await this.msalInstance.handleRedirectPromise();

          if (response) {
            this.account = response.account;
            return { success: true, account: this.account };
          }
        } catch (redirectError) {
          // Ignore redirect errors (e.g., no_token_request_cache_error)
          // This is expected when there's no ongoing redirect flow
        }

        // Check if user is already logged in
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.account = accounts[0];
          this.msalInstance.setActiveAccount(this.account);
          return { success: true, account: this.account };
        }

        return { success: false, message: "No active account" };
      } catch (error) {
        console.error("❌ MSAL initialization error:", error);
        throw error;
      }
    })();

    return this.initializePromise;
  }

  /**
   * Login with popup
   */
  async loginPopup() {
    try {
      await this.initialize();

      const response = await this.msalInstance.loginPopup(loginRequest);

      this.account = response.account;
      this.msalInstance.setActiveAccount(this.account);

      return {
        success: true,
        account: {
          username: this.account.username,
          name: this.account.name,
          localAccountId: this.account.localAccountId,
          homeAccountId: this.account.homeAccountId,
        },
      };
    } catch (error) {
      console.error("❌ Login error:", error);

      if (error.errorCode === "user_cancelled") {
        return {
          success: false,
          error: "Login was cancelled",
        };
      }

      return {
        success: false,
        error: error.message || "Login failed",
      };
    }
  }

  /**
   * Login with redirect
   */
  async loginRedirect() {
    try {
      await this.initialize();
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("❌ Login redirect error:", error);
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout() {
    try {
      if (!this.msalInstance) {
        await this.initialize();
      }

      const account = this.account || this.msalInstance.getActiveAccount();

      await this.msalInstance.logoutPopup({
        account: account,
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });

      this.account = null;

      return { success: true };
    } catch (error) {
      console.error("❌ Logout error:", error);

      // Force logout locally
      this.account = null;
      if (this.msalInstance) {
        this.msalInstance.setActiveAccount(null);
      }

      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get access token
   */
  async getAccessToken(scopes = ["User.Read"]) {
    try {
      await this.initialize();

      if (!this.account) {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.account = accounts[0];
          this.msalInstance.setActiveAccount(this.account);
        } else {
          // Fall back to SSO session (no MSAL account for SSO users)
          const ssoRaw = localStorage.getItem("userProfileData");
          if (ssoRaw) {
            try {
              const ssoProfile = JSON.parse(ssoRaw);
              if (ssoProfile?.source === "sso") {
                const token = localStorage.getItem("auth_token");
                if (token) {
                  return { success: true, accessToken: token };
                }
              }
            } catch { /* ignore */ }
          }
          throw new Error("No active account. Please login first.");
        }
      }

      // Try to get token silently
      try {
        const response = await this.msalInstance.acquireTokenSilent({
          scopes: scopes,
          account: this.account,
        });

        return {
          success: true,
          accessToken: response.accessToken,
        };
      } catch (silentError) {
        // If silent token acquisition fails, try with popup
        if (silentError.name === "InteractionRequiredAuthError") {
          const response = await this.msalInstance.acquireTokenPopup({
            scopes: scopes,
            account: this.account,
          });

          return {
            success: true,
            accessToken: response.accessToken,
          };
        }

        throw silentError;
      }
    } catch (error) {
      console.error("❌ Get access token error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get current user account
   */
  async getAccount() {
    try {
      await this.initialize();

      if (this.account) {
        return {
          success: true,
          account: {
            username: this.account.username,
            name: this.account.name,
            localAccountId: this.account.localAccountId,
            homeAccountId: this.account.homeAccountId,
          },
        };
      }

      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.account = accounts[0];
        this.msalInstance.setActiveAccount(this.account);

        return {
          success: true,
          account: {
            username: this.account.username,
            name: this.account.name,
            localAccountId: this.account.localAccountId,
            homeAccountId: this.account.homeAccountId,
          },
        };
      }

      return {
        success: false,
        message: "No account found",
      };
    } catch (error) {
      console.error("❌ Get account error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get user ID
   */
  async getUserId() {
    const result = await this.getAccount();
    if (result.success && result.account) {
      return result.account.localAccountId || result.account.homeAccountId;
    }
    // Fall back to SSO profile id
    const ssoRaw = localStorage.getItem("userProfileData");
    if (ssoRaw) {
      try {
        const ssoProfile = JSON.parse(ssoRaw);
        if (ssoProfile?.source === "sso" && ssoProfile?.id) {
          return ssoProfile.id;
        }
      } catch { /* ignore */ }
    }
    return null;
  }

  /**
   * Check authentication status
   */
  async getAuthStatus() {
    try {
      await this.initialize();

      const accounts = this.msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        const account = accounts[0];
        return {
          isAuthenticated: true,
          account: {
            username: account.username,
            name: account.name,
            localAccountId: account.localAccountId,
            homeAccountId: account.homeAccountId,
          },
        };
      }

      return {
        isAuthenticated: false,
        account: null,
      };
    } catch (error) {
      console.error("❌ Get auth status error:", error);
      return {
        isAuthenticated: false,
        account: null,
        error: error.message,
      };
    }
  }
}

// Create singleton instance
const webAuthService = new WebAuthService();

export default webAuthService;
